import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const OrganizationContext = createContext(null);

export function OrganizationProvider({ children }) {
    const { user } = useAuth();
    const [organizations, setOrganizations] = useState([]);
    const [currentOrg, setCurrentOrg] = useState(null);
    // Qaysi foydalanuvchi uchun tashkilotlar yuklab bo'lingan. `loading` shundan
    // sinxron hisoblanadi: user paydo bo'lgan zahoti (hali tashkilot tanlanmagan
    // render'da ham) true bo'ladi, shu user uchun qayta yuklashda esa yoqilmaydi
    // (aks holda rail yo'qolib, sidebar sakraydi).
    const [loadedKey, setLoadedKey] = useState(null);

    // `user` obyekti har safar yangilanadi (token refresh, favorite va h.k.).
    // Faqat tashkilotlarga ta'sir qiladigan maydonlar o'zgarganda qayta yuklaymiz.
    const userKey = user
        ? `${user.id}|${user.is_superadmin ? 1 : 0}|${user.can_manage_organizations ? 1 : 0}|${(user.organization_ids || []).join(',')}`
        : '';
    const loading = !!user && loadedKey !== String(user.id);

    const fetchOrganizations = useCallback(async () => {
        if (!userKey) {
            setOrganizations([]);
            setCurrentOrg(null);
            setLoadedKey(null);
            return;
        }

        try {
            const res = await api.get('/organizations');
            const orgs = res.data || [];
            setOrganizations(orgs);

            if (orgs.length > 0) {
                const savedOrgId = localStorage.getItem('active_org_id');
                const matchedOrg = orgs.find(o => o.id === savedOrgId);
                const active = matchedOrg || orgs[0];
                setCurrentOrg(active);
                localStorage.setItem('active_org_id', active.id);
            } else {
                setCurrentOrg(null);
                localStorage.removeItem('active_org_id');
            }
        } catch (err) {
            console.error('Failed to fetch organizations:', err);
        } finally {
            setLoadedKey(userKey.split('|')[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userKey]);

    useEffect(() => {
        fetchOrganizations();
    }, [fetchOrganizations]);

    const switchOrg = useCallback((orgId) => {
        const found = organizations.find(o => o.id === orgId);
        if (found) {
            setCurrentOrg(found);
            localStorage.setItem('active_org_id', found.id);
        }
    }, [organizations]);

    const getLocalizedName = useCallback((org, lang = 'uz') => {
        if (!org) return '';
        if (lang === 'en') return org.name_en || org.name_ru || org.name_uz || '';
        if (lang === 'ru') return org.name_ru || org.name_uz || org.name_en || '';
        return org.name_uz || org.name_ru || org.name_en || '';
    }, []);

    const value = {
        organizations,
        currentOrg,
        currentOrgId: currentOrg?.id || null,
        switchOrg,
        refreshOrganizations: fetchOrganizations,
        getLocalizedName,
        loading,
    };

    return (
        <OrganizationContext.Provider value={value}>
            {children}
        </OrganizationContext.Provider>
    );
}

export function useOrganization() {
    const ctx = useContext(OrganizationContext);
    if (!ctx) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    return ctx;
}
